import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { User, Moon, Sun, Lock, HelpCircle } from 'lucide-react';
import AuthService from '../Api_Module/Api_Services/AuthService';
import ConfirmationModal from '../components/ConfirmationModal';
import NotificationModal from '../components/NotificationModal';

interface UserData {
  email: string;
  name: string;
  subscriptionStatus?: string;
  product?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  planInterval?: string;
  amountTotal?: number;
  currency?: string;
  stripeSubscriptionId?: string;
  cancelAtPeriodEnd?: boolean;
  receiptNumber?: string;
  referral_code?: string;
  referral_link?: string;
  totalReferrals?: number;
  referralEarnings?: number;
}

export default function Profile() {
  const { theme, toggleTheme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCancellation, setPendingCancellation] = useState<{ immediate: boolean; message: string; title: string } | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; title: string; message: string } | null>(null);
  const navigate = useNavigate();

  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      const result = await AuthService.getUserData();
      console.log('User Data Response:', result);
      if (result) {
        // If the backend returns a referral code but no referral_link, copy the code into referral_link.
        try {
          if (result.referral_code && !result.referral_link) {
            result.referral_link = result.referral_code;
          }
        } catch (err) {
          // ignore any mutation errors
        }
        setUserData(result);
      } else {
        console.error('Failed to fetch user data');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleCancelSubscription = async (immediate = false) => {
    if (!userData?.stripeSubscriptionId) return;
    
    const title = immediate ? 'Cancel Subscription Immediately' : 'Cancel Subscription';
    const message = immediate 
      ? 'Are you sure you want to cancel your subscription immediately? Your access will end right away and no refund will be provided.'
      : 'Are you sure you want to cancel your subscription? You will retain access until the end of your current billing period.';
    
    setPendingCancellation({ immediate, message, title });
    setShowConfirmModal(true);
  }

  const confirmCancellation = async () => {
    if (!userData?.stripeSubscriptionId || !pendingCancellation) return;
    
    try {
      setIsCancelling(true);
      setShowConfirmModal(false);
      
      if (pendingCancellation.immediate) {
        await AuthService.cancelSubscriptionImmediate(userData.stripeSubscriptionId);
      } else {
        await AuthService.cancelSubscription(userData.stripeSubscriptionId);
      }
      
      await fetchUserData();
      
      setNotification({
        type: 'success',
        title: 'Subscription Cancelled',
        message: pendingCancellation.immediate 
          ? 'Your subscription has been cancelled immediately.' 
          : 'Your subscription will cancel at the end of your current billing period.'
      });
      setShowNotification(true);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setNotification({
        type: 'error',
        title: 'Cancellation Failed',
        message: 'Failed to cancel subscription. Please try again or contact support.'
      });
      setShowNotification(true);
    } finally {
      setIsCancelling(false);
      setPendingCancellation(null);
    }
  }

  const handleModalClose = () => {
    setShowConfirmModal(false);
    setPendingCancellation(null);
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'USD'
    }).format(amount / 100); // Convert cents to dollars
  }

  React.useEffect(() => {
    fetchUserData();
  }, []);

  const cardBase = theme === 'dark'
    ? 'bg-theme-primary border border-theme-secondary'
    : 'bg-white border border-gray-200';

  const titleBase = theme === 'dark' ? 'text-gray-100' : 'text-gray-800';
  const textBase = theme === 'dark' ? 'text-theme-secondary' : 'text-gray-700';

  // Copy referral_code into referral_link (state) and copy to clipboard
  const handleCopyReferral = async () => {
    if (!userData) return;

    let link = userData.referral_link || '';
    // If there's no referral_link but referral_code exists, copy code into referral_link in state
    if (!link && userData.referral_code) {
      link = userData.referral_code;
      try {
        setUserData(prev => prev ? { ...prev, referral_link: link } : prev);
      } catch {
        // ignore state update errors
      }
    }

    if (!link) {
      setNotification({ type: 'error', title: 'No Referral', message: 'No referral code/link available to copy.' });
      setShowNotification(true);
      return;
    }

    try {
      await navigator.clipboard.writeText(link);
      setNotification({ type: 'success', title: 'Copied', message: 'Referral copied to clipboard.' });
      setShowNotification(true);
    } catch (err) {
      console.error('Copy failed', err);
      setNotification({ type: 'error', title: 'Copy Failed', message: 'Unable to copy to clipboard.' });
      setShowNotification(true);
    }
  };

  return (
    isLoading ? (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-16 w-16"></div>
      </div>
    ) : (
      <div className={`min-h-[60vh] ${theme === 'dark' ? 'bg-gray-950' : 'bg-gray-50'}`}>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-6">
            <div className={`p-2 rounded-full ${theme === 'dark' ? 'bg-theme-secondary' : 'bg-white border border-gray-200'}`}>
              <User className="w-6 h-6 active-text" />
            </div>
            <h1 className={`text-2xl font-semibold ${titleBase}`}>My Account</h1>
          </div>

          <div className="space-y-6">
            {/* Preferences */}
            <section className={`rounded-lg p-5 ${cardBase}`}>
              <h2 className={`text-lg font-semibold mb-4 ${titleBase}`}>Preferences</h2>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md ${theme === 'dark' ? 'bg-theme-secondary text-gray-100' : 'bg-gray-100 text-gray-800'}`}>
                    {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                    {theme === 'dark' ? 'Dark' : 'Light'}
                  </span>
                </div>
                <button
                  onClick={toggleTheme}
                  className="inline-flex items-center outline-none gap-2 px-3 py-1.5 rounded-md btn active text-theme-primary text-sm font-medium"
                >
                  {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  Switch to {theme === 'dark' ? 'Light' : 'Dark'}
                </button>
              </div>
            </section>
  
            {/* User Information */}
            <section className={`rounded-lg p-5 ${cardBase}`}>
              <h2 className={`text-lg font-semibold mb-2 ${titleBase}`}>User Information</h2>
              <div className="space-y-2">
                <p className={textBase}>Name: <span className="font-medium">{userData?.name || 'N/A'}</span></p>
                <p className={textBase}>Email: <span className="font-medium">{userData?.email}</span></p>
              </div>
            </section>
  
            {/* Referral */}
            <section className={`rounded-lg p-5 ${cardBase}`}>
              <h2 className={`text-lg font-semibold mb-2 ${titleBase}`}>Referral</h2>
              {userData?.referral_code || userData?.referral_link ? (
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className={textBase}>Code: <span className="font-medium break-all">{userData?.referral_code || 'N/A'}</span></p>
                    <p className={textBase}>Total Referrals: <span className="font-medium">{userData?.totalReferrals ?? 0}</span></p>
                    {/* <p className={textBase}>Total Earnings: <span className="font-medium">{userData?.referralEarnings ?? 0}Pts.</span></p> */}
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={handleCopyReferral}
                      className="px-3 py-2 rounded-md text-sm font-medium btn active  text-theme-primary"
                    >
                      Get Share URL
                    </button>
                  </div>
                  
                </div>
              ) : (
                <p className={textBase}>No referral code available.</p>
              )}
            </section>
  
            {/* Manage Subscription */}
            <section className={`rounded-lg p-5 ${cardBase}`}>
              <h2 className={`text-lg font-semibold mb-4 ${titleBase}`}>Manage Subscription</h2>
  
              {userData?.subscriptionStatus === 'active' || userData?.subscriptionStatus === 'trialing' ? (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-green-900/20 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className={`font-medium ${theme === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
                        Active Subscription
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                      <div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-600'}`}>
                          Plan: <span className="font-medium">{userData.product}</span>
                        </p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-600'}`}>
                          Amount: <span className="font-medium">{formatAmount(userData.amountTotal || 0, userData.currency || 'usd')}</span>
                        </p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-600'}`}>
                          Billing: <span className="font-medium">{userData.planInterval === 'month' ? 'Monthly' : 'Yearly'}</span>
                        </p>
                      </div>
                      <div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-600'}`}>
                          Current Period: <span className="font-medium">{formatDate(userData.currentPeriodStart || '')}</span>
                        </p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-600'}`}>
                          Next Billing: <span className="font-medium">{formatDate(userData.currentPeriodEnd || '')}</span>
                        </p>
                        {userData.receiptNumber && (
                          <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-600'}`}>
                            Receipt: <span className="font-medium">{userData.receiptNumber}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
  
                  {userData.cancelAtPeriodEnd ? (
                    <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-yellow-900/20 border border-yellow-500/30' : 'bg-yellow-50 border border-yellow-200'}`}>
                      <p className={`text-sm ${theme === 'dark' ? 'text-yellow-300' : 'text-yellow-700'}`}>
                        Your subscription will cancel at the end of the current billing period on {formatDate(userData.currentPeriodEnd || '')}.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => handleCancelSubscription(false)}
                          disabled={isCancelling}
                          className={`px-4 py-2 rounded-lg text-sm font-medium btn ${
                            isCancelling 
                              ? 'bg-gray-400 cursor-not-allowed' 
                              : 'bg-yellow-600 hover:bg-yellow-700'
                          }`}
                        >
                          {isCancelling ? 'Processing...' : 'Cancel'}
                        </button>
                        {/* <button
                          onClick={() => handleCancelSubscription(true)}
                          disabled={isCancelling}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isCancelling 
                              ? 'bg-gray-400 cursor-not-allowed text-theme-primary' 
                              : 'bg-red-600 hover:bg-red-700 text-theme-primary'
                          }`}
                        >
                          {isCancelling ? 'Processing...' : 'Cancel Immediately'}
                        </button> */}
                        <button
                          onClick={() => navigate('/pricing')}
                          className="px-4 py-2 rounded-lg text-sm font-medium btn active text-theme-primary transition-colors"
                        >
                          Manage Plan
                        </button>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <p>• <strong>Cancel at Period End:</strong> Keep access until {formatDate(userData.currentPeriodEnd || '')}</p>
                        <p>• <strong>Cancel Immediately:</strong> Lose access right away, no refund</p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className={textBase}>There is no active subscription on this account.</p>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-theme-primary transition-colors"
                  >
                    View Plans
                  </button>
                </div>
              )}
            </section>
  
            {/* Get Support */}
            <section className={`rounded-lg p-5 ${cardBase}`}>
              <h2 className={`text-lg font-semibold mb-2 ${titleBase}`}>Get Support</h2>
              <p className={`${textBase} mb-2`}>Here's how to get support:</p>
              <ol className={`list-decimal pl-5 space-y-1 ${textBase}`}>
                <li>Send an email to <a href="mailto:support@stockfortress.com" className="text-blue-600 hover:underline">support@stockfortress.com</a>.</li>
                {/* <li>Send a message via the <a href="#" className="text-blue-600 hover:underline">contact form</a>.</li> */}
              </ol>
            </section>
          </div>

          <div className="mt-8">
            <button
              onClick={() => navigate(-1)}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-theme-muted dark:hover:text-gray-200"
            >
              ← Back
            </button>
          </div>
        </div>

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={handleModalClose}
          onConfirm={confirmCancellation}
          title={pendingCancellation?.title || ''}
          message={pendingCancellation?.message || ''}
          confirmText={pendingCancellation?.immediate ? 'Cancel Immediately' : 'Cancel at Period End'}
          cancelText="Keep Subscription"
          isDestructive={true}
          isLoading={isCancelling}
        />

        {/* Notification Modal */}
        <NotificationModal
          isOpen={showNotification}
          onClose={() => setShowNotification(false)}
          type={notification?.type || 'success'}
          title={notification?.title || ''}
          message={notification?.message || ''}
        />
      </div>
    )
  );
}
